import React, { useState, useCallback } from 'react';
import { Table, Tag, Typography, Row, Col, Pagination, Space, Spin, Tooltip, Rate, Button, Modal } from 'antd';
import { LineChartOutlined, TrophyOutlined } from '@ant-design/icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { useDispatch } from 'react-redux';
import { handlePaginationChange, fetchListingChildren } from '../actions';
import ProductImagesGallery from '../../../components/ProductImagesGallery/ProductImagesGallery';
import "./Listings.css";

const { Text, Paragraph } = Typography;

const ListingsList = ({
    listings,
    isLoading,
    pagination,
    expandedRowKeys,
    onExpandedRowsChange
}) => {
    const dispatch = useDispatch();
    const [childrenData, setChildrenData] = useState({});
    const [loadingChildren, setLoadingChildren] = useState({});

    // Chart modal states
    const [isChartModalVisible, setIsChartModalVisible] = useState(false);
    const [chartData, setChartData] = useState([]);
    const [chartTitle, setChartTitle] = useState('');
    const [chartType, setChartType] = useState(''); // 'quantity' or 'rank'
    const [selectedRecord, setSelectedRecord] = useState(null);

    const handleTableChange = (pagination) => {
        dispatch(handlePaginationChange(pagination.current, pagination.pageSize));
    };

    const getStatusTag = (status) => {
        let color = 'default';
        let text = status;

        switch (status) {
            case 'ACTIVE':
                color = 'green';
                text = 'Active';
                break;
            case 'INACTIVE':
                color = 'orange';
                text = 'Inactive';
                break;
            case 'DELETED':
                color = 'red';
                text = 'Deleted';
                break;
            default:
                color = 'default';
        }

        return <Tag style={{ width: 110, textAlign: 'center' }} color={color}>{text}</Tag>;
    };

    const getFulfillmentChannelTag = (channel) => {
        return channel === 'FBA' ?
            <Tag color="orange" style={{ textAlign:'center', fontSize: 12, width: 43, marginRight: 0 }}>FBA</Tag> :
            channel === 'FBM' ?
                <Tag color="blue" style={{ textAlign:'center', fontSize: 12, width: 43, marginRight: 0 }}>FBM</Tag> :
                <Tag color="default" style={{ textAlign:'center', fontSize: 12 }}>{channel || '-'}</Tag>;
    };

    const getSellerAccountText = (listing) => {
        if (listing.sellerAccount && listing.sellerAccount.name) {
            return listing.sellerAccount.name;
        }
        return 'Unknown';
    };

    // Function to generate mockup rating data
    const getMockupRating = (sku) => {
        // Generate consistent mockup data based on SKU hash
        const hash = sku.split('').reduce((a, b) => {
            a = ((a << 5) - a) + b.charCodeAt(0);
            return a & a;
        }, 0);

        const ratings = [3.5, 4.0, 4.2, 4.5, 4.7, 4.8, 4.9];
        const reviewCounts = [15, 28, 45, 67, 89, 123, 156, 234, 345, 567, 789, 1234];

        const rating = ratings[Math.abs(hash) % ratings.length];
        const reviewCount = reviewCounts[Math.abs(hash) % reviewCounts.length];

        return { rating, reviewCount };
    };

    // Function to generate mockup quantity ordered chart data
    const generateQuantityOrderedChartData = (sku) => {
        const data = [];
        const today = new Date();
        const hash = sku.split('').reduce((a, b) => {
            a = ((a << 5) - a) + b.charCodeAt(0);
            return a & a;
        }, 0);

        for (let i = 29; i >= 0; i--) {
            const date = new Date(today);
            date.setDate(date.getDate() - i);

            // Generate consistent but varying quantity data
            const baseQuantity = 5 + (Math.abs(hash + i) % 15);
            const variation = Math.sin((hash + i) / 3) * 3;
            const quantity = Math.max(0, Math.round(baseQuantity + variation));

            data.push({
                date: `${date.getMonth() + 1}/${date.getDate()}`,
                fullDate: date.toISOString().split('T')[0],
                value: quantity
            });
        }
        return data;
    };

    // Function to generate mockup main rank chart data
    const generateMainRankChartData = (sku) => {
        const data = [];
        const today = new Date();
        const hash = sku.split('').reduce((a, b) => {
            a = ((a << 5) - a) + b.charCodeAt(0);
            return a & a;
        }, 0);

        const baseRank = 150000 + (Math.abs(hash) % 100000);

        for (let i = 29; i >= 0; i--) {
            const date = new Date(today);
            date.setDate(date.getDate() - i);

            // Generate rank variation (lower rank = better)
            const variation = Math.sin((hash + i) / 5) * 20000;
            const rank = Math.max(1000, Math.round(baseRank + variation));

            data.push({
                date: `${date.getMonth() + 1}/${date.getDate()}`,
                fullDate: date.toISOString().split('T')[0],
                value: rank
            });
        }
        return data;
    };

    // Handle chart button clicks
    const handleQuantityChart = (record) => {
        const data = generateQuantityOrderedChartData(record.sku);
        setChartData(data);
        setChartTitle(`Quantity Ordered Trend - ${record.sku}`);
        setChartType('quantity');
        setSelectedRecord(record);
        setIsChartModalVisible(true);
    };

    const handleRankChart = (record) => {
        const data = generateMainRankChartData(record.sku);
        setChartData(data);
        setChartTitle(`Best Sellers Rank Trend - ${record.sku}`);
        setChartType('rank');
        setSelectedRecord(record);
        setIsChartModalVisible(true);
    };

    // Function to handle row expansion
    const handleExpand = async (expanded, record) => {
        if (expanded && record.childrenCount > 0) {
            // Load children if not already loaded
            if (!childrenData[record.sku]) {
                setLoadingChildren(prev => ({ ...prev, [record.sku]: true }));
                try {
                    const children = await dispatch(fetchListingChildren(record.sku));
                    setChildrenData(prev => ({ ...prev, [record.sku]: children }));
                } catch (error) {
                    console.error('Error loading children:', error);
                } finally {
                    setLoadingChildren(prev => ({ ...prev, [record.sku]: false }));
                }
            }
        }
    };

    // Expanded row render function
    const expandedRowRender = (record) => {
        const children = childrenData[record.sku] || [];
        const loading = loadingChildren[record.sku];

        if (loading) {
            return <Spin style={{ display: 'block', textAlign: 'center', padding: '20px' }} />;
        }

        const childrenColumns = [
            {
                title: 'Childs',
                dataIndex: 'childrenCount',
                key: 'childrenCount',
                width: 80,
                align: 'center',
                render: (count) => <a style={{ color: 'unset', textDecoration: 'underline' }}>30</a>
            },
            {
                title: <Tooltip title="Mã SKU">SKU / ASIN</Tooltip>,
                dataIndex: 'sku',
                key: 'sku',
                ellipsis: true,
                render: (text, record) => (
                    <Row style={{ flexFlow: 'nowrap' }} align='middle' gutter={8}>
                        <Col>
                            <ProductImagesGallery skuItem={record} maxVisibleImages={1} showRemainingCount={false} imageSize={30} />
                        </Col>
                        <Col>
                            <a target='_blank' className="no-line-through" href={`https://www.amazon.com/dp/${record.asin}`}>{record.title}</a><br></br>
                            <Paragraph style={{ marginBottom: 0, fontSize: '12px' }} ellipsis={{ rows: 1 }} className="no-line-through"><b>ASIN: </b>{record.asin || '-'}</Paragraph>
                            <Paragraph style={{ marginBottom: 0, fontSize: '12px' }} ellipsis={{ rows: 1 }} className="no-line-through"><b>SKU: </b>{text || '-'}</Paragraph>
                        </Col>
                    </Row>
                )
            },
            {
                title: 'Rating',
                key: 'rating',
                width: 140,
                align: 'center',
                render: (_, record) => {
                    const { rating, reviewCount } = getMockupRating(record.sku);
                    return (
                        <div style={{ textAlign: 'center' }}>
                            <Rate
                                disabled
                                allowHalf
                                value={rating}
                                style={{ fontSize: '12px', color: '#faad14' }}
                            />
                            <div style={{ fontSize: '10px', color: '#999', marginTop: '1px' }}>
                                ({reviewCount.toLocaleString()} reviews)
                            </div>
                        </div>
                    );
                }
            },
            {
                title: '#Best Sellers Rank',
                key: 'bestSellersRank',
                width: 170,
                render: (_, record) => {
                    return (
                        <div>
                            {
                                <div style={{ fontSize: '12px', color: '#999', marginBottom: '2px' }}>
                                    {/* {record.mainRankName} */}
                                    Clothing, Shoes & Jewelry
                                </div>
                            }
                            {
                                <div style={{ fontWeight: 'bold' }}>
                                    {/* #{record.mainRank.toLocaleString()} */}
                                    #201,345
                                </div>
                            }
                        </div>
                    );
                    if (!record.mainRank && !record.mainRankName) {
                        return <span style={{ color: '#999' }}>-</span>;
                    }

                    return (
                        <div>
                            {record.mainRankName && (
                                <div style={{ fontSize: '12px', color: '#999', marginBottom: '2px' }}>
                                    {record.mainRankName}
                                </div>
                            )}
                            {record.mainRank && (
                                <div style={{ fontWeight: 'bold' }}>
                                    #{record.mainRank.toLocaleString()}
                                </div>
                            )}
                        </div>
                    );
                }
            },
            {
                title: 'Quantity',
                dataIndex: 'quantityOrdered',
                key: 'quantityOrdered',
                width: 100,
                align: 'center',
                render: (qty) => <strong style={{ fontSize: '13px', color: '#1677ff' }}>{qty || 0}</strong>
            },
            {
                title: 'Charts',
                key: 'charts',
                width: 80,
                align: 'center',
                render: (_, record) => (
                    <Space size={5}>
                        <Button
                            size="small"
                            icon={<LineChartOutlined />}
                            onClick={() => handleQuantityChart(record)}
                            title="Quantity Ordered Trend"
                        />
                        <Button
                            size="small"
                            icon={<TrophyOutlined />}
                            onClick={() => handleRankChart(record)}
                            title="Best Sellers Rank Trend"
                        />
                    </Space>
                )
            },
            {
                title: 'Seller Account',
                dataIndex: 'sellerAccount',
                key: 'sellerAccount',
                width: 120,
                render: (sellerAccount, record) => (
                    <span className="no-line-through">{getSellerAccountText(record)}</span>
                ),
            },
            {
                title: 'Channel',
                dataIndex: 'fulfillmentChannel',
                key: 'fulfillmentChannel',
                width: 90,
                align: 'center',
                render: (channel) => (
                    <span className="no-line-through">
                        {getFulfillmentChannelTag(channel)}
                    </span>
                )
            },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                width: 140,
                align: 'center',
                render: (status) => getStatusTag(status)
            }
        ];

        return (
            <div style={{ position: 'relative', margin: '-8px -16px' }}>
                <div style={{ position: 'absolute', zIndex: 10, top: 0, left: 0, right: 0, bottom: 0, boxShadow: 'inset 13px 5px 14px 3px rgba(0, 0, 0, 0.15)' }}></div>
                <Table
                    bordered={false}
                    columns={childrenColumns}
                    dataSource={children.map(child => ({ ...child, key: child.id }))}
                    pagination={false}
                    rowClassName={getRowClassName}
                    size="small"
                    expandable={{
                        expandedRowKeys,
                        onExpandedRowsChange,
                        onExpand: handleExpand,
                        expandedRowRender,
                        columnWidth: 128,
                        rowExpandable: (record) => record.childrenCount > 0,
                    }}
                    // style={{ marginLeft: '80px' }}
                    components={{
                        header: {
                            cell: (props) => (
                                <th
                                    {...props}
                                    style={{
                                        ...props.style,
                                        borderRadius: 3
                                    }}
                                />
                            )
                        }
                    }}
                />
            </div>
        );
    };

    // Function to set row className for alternating colors
    const getRowClassName = useCallback((record, index) => {
        return index % 2 === 0 ? 'table-row-even' : 'table-row-odd';
    }, []);

    const columns = [
        // {
        //     title: '#',
        //     key: 'index',
        //     width: 60,
        //     render: (_, __, index) => (
        //         <span style={{ fontSize: 12 }}>
        //             {(pagination.current - 1) * pagination.pageSize + index + 1}
        //         </span>
        //     )
        // },
        {
            title: 'Childs',
            dataIndex: 'childrenCount',
            key: 'childrenCount',
            width: 80,
            align: 'center',
            render: (count) => <a style={{ color: 'unset', textDecoration: 'underline' }}>{count || 0}</a>
        },
        {
            title: <Tooltip title="Mã SKU">SKU / ASIN</Tooltip>,
            dataIndex: 'sku',
            key: 'sku',
            // width: 370,
            ellipsis: true,
            render: (text, record) =>
                <Row style={{ flexFlow: 'nowrap' }} align='middle' gutter={8}>
                    <Col>
                        <ProductImagesGallery skuItem={record} maxVisibleImages={1} showRemainingCount={false} imageSize={49} />
                    </Col>
                    <Col>
                        <a target='_blank' className="no-line-through" href={`https://www.amazon.com/dp/${record.asin}`}>{record.title}</a><br></br>
                        <Paragraph style={{ marginBottom: 0, fontSize: 12 }} ellipsis={{ rows: 1 }} className="no-line-through"><b>ASIN:</b> {record.asin || '-'}</Paragraph>
                        <Paragraph style={{ marginBottom: 0, fontSize: 12 }} ellipsis={{ rows: 1 }} className="no-line-through"><b>SKU:</b> {text || '-'}</Paragraph>
                    </Col>
                </Row>
        },
        {
            title: 'Rating',
            key: 'rating',
            width: 140,
            align: 'center',
            render: (_, record) => {
                const { rating, reviewCount } = getMockupRating(record.sku);
                return (
                    <div style={{ textAlign: 'center' }}>
                        <Rate
                            disabled
                            allowHalf
                            value={rating}
                            style={{ fontSize: '12px', color: '#faad14' }}
                        />
                        <div style={{ fontSize: '10px', color: '#999', marginTop: '1px' }}>
                            ({reviewCount.toLocaleString()} reviews)
                        </div>
                    </div>
                );
            }
        },
        {
            title: '#Best Sellers Rank',
            key: 'bestSellersRank',
            width: 170,
            render: (_, record) => {
                return (
                    <div>
                        {
                            <div style={{ fontSize: '12px', color: '#999', marginBottom: '2px' }}>
                                {/* {record.mainRankName} */}
                                Clothing, Shoes & Jewelry
                            </div>
                        }
                        {
                            <div style={{ fontWeight: 'bold' }}>
                                {/* #{record.mainRank.toLocaleString()} */}
                                #201,345
                            </div>
                        }
                    </div>
                );
                if (!record.mainRank && !record.mainRankName) {
                    return <span style={{ color: '#999' }}>-</span>;
                }

                return (
                    <div>
                        {record.mainRankName && (
                            <div style={{ fontSize: '12px', color: '#999', marginBottom: '2px' }}>
                                {record.mainRankName}
                            </div>
                        )}
                        {record.mainRank && (
                            <div style={{ fontWeight: 'bold' }}>
                                #{record.mainRank.toLocaleString()}
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            title: 'Quantity',
            dataIndex: 'quantityOrdered',
            key: 'quantityOrdered',
            width: 100,
            align: 'center',
            render: (qty) => <strong style={{ color: '#1677ff' }}>{qty || 0}</strong>
        },
        {
            title: 'Charts',
            key: 'charts',
            width: 80,
            align: 'center',
            render: (_, record) => (
                <Space size={5}>
                    <Button
                        size="small"
                        icon={<LineChartOutlined />}
                        onClick={() => handleQuantityChart(record)}
                        title="Quantity Ordered Trend"
                    />
                    <Button
                        size="small"
                        icon={<TrophyOutlined />}
                        onClick={() => handleRankChart(record)}
                        title="Best Sellers Rank Trend"
                    />
                </Space>
            )
        },
        {
            title: 'Seller Account',
            dataIndex: 'sellerAccount',
            key: 'sellerAccount',
            width: 120,
            render: (sellerAccount, record) => (
                <span className="no-line-through">{getSellerAccountText(record)}</span>
            ),
        },
        {
            title: 'Channel',
            dataIndex: 'fulfillmentChannel',
            key: 'fulfillmentChannel',
            width: 90,
            align: 'center',
            render: (channel) => (
                <span className="no-line-through">
                    {getFulfillmentChannelTag(channel)}
                </span>
            )
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 140,
            align: 'center',
            render: (status) => getStatusTag(status)
        }
    ];

    return (
        <>
            <Row justify="space-between" align="middle" style={{ marginTop: 13, marginBottom: 10 }}>
                <Col>
                    <Space direction='horizontal' size={4}>
                        <Text strong style={{ fontSize: 14 }}>
                            Total Listings:
                        </Text>
                        <Pagination
                            size="small"
                            current={pagination?.current || 1}
                            total={pagination?.total || 0}
                            pageSize={pagination?.pageSize || 25}
                            onChange={(page, pageSize) => dispatch(handlePaginationChange(page, pageSize))}
                            showSizeChanger={false}
                            showQuickJumper={false}
                            showTotal={(total, range) =>
                                `${range[0]}-${range[1]} of ${total}`
                            }
                        />
                    </Space>
                </Col>
            </Row>

            <Table
                columns={columns}
                dataSource={listings.map(listing => ({ ...listing, key: listing.id }))}
                loading={isLoading}
                rowClassName={getRowClassName}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showSizeChanger: true,
                    size: 'small',
                    showQuickJumper: true,
                    pageSizeOptions: ['10', '25', '50', '100'],
                    showTotal: (total) => `Total ${total} listings`
                }}
                onChange={handleTableChange}
                scroll={{ x: 1500 }}
                expandable={{
                    expandedRowKeys,
                    onExpandedRowsChange,
                    onExpand: handleExpand,
                    expandedRowRender,
                    rowExpandable: (record) => record.childrenCount > 0,
                }}
            />

            {/* Chart Modal */}
            <Modal
                title={chartTitle}
                open={isChartModalVisible}
                onCancel={() => setIsChartModalVisible(false)}
                footer={null}
                width={800}
                destroyOnClose
            >
                {chartData.length > 0 && (
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                                dataKey="date"
                                tick={{ fontSize: 12 }}
                            />
                            <YAxis 
                                tick={{ fontSize: 12 }}
                                tickFormatter={(value) => {
                                    if (chartType === 'rank') {
                                        return `#${parseInt(value).toLocaleString()}`;
                                    }
                                    return value;
                                }}
                            />
                            <RechartsTooltip
                                formatter={(value, name, props) => {
                                    const date = new Date(props.payload.fullDate);
                                    const formattedDate = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
                                    
                                    if (chartType === 'quantity') {
                                        return [`${value} units`, 'Quantity Ordered'];
                                    } else {
                                        return [`#${value.toLocaleString()}`, 'Best Sellers Rank'];
                                    }
                                }}
                                labelFormatter={(label, payload) => {
                                    if (payload && payload.length > 0) {
                                        const date = new Date(payload[0].payload.fullDate);
                                        return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
                                    }
                                    return label;
                                }}
                            />
                            <Line 
                                type="monotone" 
                                dataKey="value" 
                                stroke={chartType === 'quantity' ? '#18a058' : '#1890ff'}
                                strokeWidth={2}
                                dot={{ r: 3 }}
                                activeDot={{ r: 5 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </Modal>
        </>
    );
};

export default ListingsList;